import { useTheme } from "@mui/material";
import { head, split } from "lodash";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { Box } from "@/Components/v2/atoms/Box";
import { DashboardCard } from "@/Components/v2/atoms/Card/DashboardCard";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { colors } from "@/Components/v2/utils/brand-colors";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Layer } from "@/Utils/hierarchy";

import { TransformedExperiment, useTestingRecommendations } from "./api/getTestingRecommendations";
import { MaitePromoWidget } from "./components/MaitePromoWidget";
import { MaiteWidget } from "./components/MaiteWidget";
import { PowerPlays } from "./components/PowerPlays";
import ScenarioBuilderCard from "./components/ScenarioBuilderCard";
import { UnlockPowerPlay } from "./components/UnlockPowerPlay";
import { getModules, Module } from "./constants/modules";
import { useGrowthCentreStore } from "./store/useGrowthCentreStore";

const GrowthCentre = () => {
  const history = useHistory();
  const theme = useTheme();
  const { buttonClicked, moduleOpened } = useAnalytics();
  const { currentOrganization, userData } = useInfo();

  // get the queryParam skipCache if it exists
  const searchParams = new URLSearchParams(window.location.search);
  const skipCache = searchParams.get("skipCache");

  const { selectedExperiment, setSelectedExperiment } = useGrowthCentreStore();

  const {
    data: testingRecommendationsData,
    isLoading,
    error,
  } = useTestingRecommendations({
    skipCache: skipCache === "true",
  });

  const modules: Module[] = getModules(theme);

  // Use the transformed experiments from the API
  const experiments: TransformedExperiment[] = testingRecommendationsData?.experiments || [];

  const hendrenTier = currentOrganization?.configuration?.hendrenTier;
  const isMaiteEnabled = hendrenTier !== "OFF";

  // Select the first experiment by default on mount
  useEffect(() => {
    if (experiments.length > 0 && !selectedExperiment) {
      setSelectedExperiment(experiments[0]?.id || null);
    }
  }, [experiments, selectedExperiment, setSelectedExperiment]);

  useEffect(() => {
    moduleOpened("growth-center", Layer.Portfolio);
  }, []);

  const handleExperimentClick = (experiment: TransformedExperiment) => {
    buttonClicked({
      buttonName: "Experiment Card",
      buttonLocation: "Growth Center - Power Plays",
      buttonType: "select",
      additionalProperties: {
        experiment_id: experiment.id,
        experiment_title: experiment.title,
        experiment_tags: experiment.tags?.map((tag) => tag.name).join(", ") || "",
      },
    });

    setSelectedExperiment(experiment.id);
  };

  return (
    <Box>
      <Stack spacing={4} sx={{ px: 4, py: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} component="h1" sx={{ mb: 1 }}>
            Hi {userData?.name ? head(split(userData.name, " ")) || "there" : "there"}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your best decisions start here. Let&apos;s get going.
          </Typography>
        </Box>

        {isMaiteEnabled ? <MaiteWidget /> : <MaitePromoWidget />}

        {isMaiteEnabled ? (
          <>
            <PowerPlays
              experiments={experiments}
              selectedExperiment={selectedExperiment}
              isLoading={isLoading}
              error={error}
              onExperimentClick={handleExperimentClick}
              rawData={testingRecommendationsData}
            />
            {/* Scenario Builder Card */}
            <ScenarioBuilderCard
              sx={{
                border: `1px solid ${theme.palette.mode === "dark" ? colors.grayBorder : colors.dropdownBorder}`,
                borderRadius: "0.5rem",
                background: theme.palette.mode === "dark" ? "#2B3033" : "#F6F9FD",
              }}
            />
          </>
        ) : (
          <>
            {/* Scenario Builder Card */}
            <ScenarioBuilderCard />
            <UnlockPowerPlay />
          </>
        )}

        <Typography variant="h6" component="h2">
          Dashboards
        </Typography>

        <Stack
          direction="row"
          spacing={2}
          sx={{
            overflowX: "auto",
            pb: 1,
            background: "none",
            mt: "12px !important",
          }}
        >
          {modules.map((module) => (
            <DashboardCard
              key={module.id}
              icon={module.icon}
              title={module.title}
              content={module.content}
              onClick={() => {
                buttonClicked({
                  buttonName: `${module.title} Module`,
                  buttonLocation: "Growth Center - Dashboard Cards",
                  buttonType: "navigation",
                  additionalProperties: {
                    module_id: module.id,
                    module_title: module.title,
                    destination: module.route,
                  },
                });

                history.push(module.route);
              }}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export default GrowthCentre;
